import { DeviceType } from '../models/device';
import { LoRaWANPointFormat, LoRaWANPointType } from '../models/lorawan';
import { ModbusPointID } from '../models/modbus';
import { SimpleStructure } from '../models/structure';
import { ProtocolPropertyLabel } from '../constants/protocol-property-label.constants';

/**
 * Parsed metadata from a tile's PointID, ready for display or search
 */
export interface ParsedTileMetadata {
  protocol: DeviceType;
  searchableText: string;
  displayText: string;
}

/**
 * Indents lines of text by adding spaces to the beginning of each line
 * @param lines - Array of text lines to indent
 * @param spaces - Number of spaces to add (default: 2)
 * @returns Indented text joined with newlines
 */
export function indentText(lines: string[], spaces: number = 2): string {
  const indent = ' '.repeat(spaces);
  return lines.map(line => indent + line).join('\n');
}

/**
 * Parses a tile's PointID into searchable and displayable metadata
 * Handles BACnet, Modbus, and LoRaWAN protocols
 */
export function parseTileMetadata(structure: SimpleStructure): ParsedTileMetadata | null {
  if (!structure || !structure.PointID) {
    return null;
  }

  switch (structure.Protocol) {
    case DeviceType.LoRaWAN:
      return parseLoRaWANMetadata(structure.PointID);
    case DeviceType.Modbus:
      return parseModbusMetadata(structure.PointID);
    case DeviceType.BACnet:
    default:
      return parseBACnetMetadata(structure.PointID);
  }
}

/**
 * Parses BACnet PointID into metadata
 * Format: ["type", "address"] e.g. ["3", "57"]
 */
export function parseBACnetMetadata(pointId: string): ParsedTileMetadata | null {
  try {
    const pointData = JSON.parse(pointId);

    if (!Array.isArray(pointData) || pointData.length < 2) {
      return null;
    }

    const [pointType, pointAddress] = pointData;
    const pointTypeName = getBACnetPointTypeName(pointType);

    const displayLines = [
      `${ProtocolPropertyLabel.BACNET_OBJECT_TYPE}: ${pointTypeName}`,
      `${ProtocolPropertyLabel.BACNET_INSTANCE_NUMBER}: ${pointAddress}`,
    ];

    return {
      protocol: DeviceType.BACnet,
      searchableText: `${pointTypeName} ${pointType} ${pointAddress}`,
      displayText: indentText(displayLines),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Parses LoRaWAN PointID into metadata
 * Format: ["type", "name", "format"] e.g. ["UP", "temperature", "num"]
 */
export function parseLoRaWANMetadata(pointId: string): ParsedTileMetadata | null {
  try {
    const pointData = JSON.parse(pointId);

    if (!Array.isArray(pointData) || pointData.length !== 3) {
      return null;
    }

    const [type, name, format] = pointData;
    const typeLabel = getLoRaWANTypeLabel(type);
    const formatLabel = getLoRaWANFormatLabel(format);

    const displayLines = [
      `${ProtocolPropertyLabel.LORAWAN_POINT_NAME}: ${name}`,
      `${ProtocolPropertyLabel.LORAWAN_DATA_SOURCE}: ${typeLabel}`,
      `${ProtocolPropertyLabel.LORAWAN_DATA_TYPE}: ${formatLabel}`,
    ];

    return {
      protocol: DeviceType.LoRaWAN,
      searchableText: `${name} ${type} ${typeLabel} ${format} ${formatLabel}`,
      displayText: indentText(displayLines),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Parses Modbus PointID into metadata
 * Format: ["type", address, endianness, writable] via ModbusConfigID
 */
export function parseModbusMetadata(pointId: string): ParsedTileMetadata | null {
  try {
    const configId = new ModbusPointID(pointId);

    const searchParts = [
      configId.Address.toString(),
      configId.Type,
      configId.getFriendlyTypeString(),
      configId.getFriendlyFunctionsString(),
      configId.getFriendlyEndiannessString(),
      configId.Writable ? 'writable' : 'readonly',
    ];

    const displayLines = [
      `${ProtocolPropertyLabel.MODBUS_ADDRESS}: ${configId.Address}`,
      `${ProtocolPropertyLabel.MODBUS_DATA_TYPE}: ${configId.getFriendlyTypeString()}`,
      `${ProtocolPropertyLabel.MODBUS_FUNCTIONS}: ${configId.getFriendlyFunctionsString()}`,
      `${ProtocolPropertyLabel.MODBUS_BYTE_ORDER}: ${configId.getFriendlyEndiannessString()}`,
    ];

    return {
      protocol: DeviceType.Modbus,
      searchableText: searchParts.join(' '),
      displayText: indentText(displayLines),
    };
  } catch (e) {
    return null;
  }
}

/**
 * Converts a BACnet point type number to a human-readable name
 */
export function getBACnetPointTypeName(pointType: string): string {
  const typeMap: Record<string, string> = {
    '0': 'Analog Input',
    '1': 'Analog Output',
    '2': 'Analog Value',
    '3': 'Binary Input',
    '4': 'Binary Output',
    '5': 'Binary Value',
    '13': 'Multistate Input',
    '14': 'Multistate Output',
    '19': 'Multistate Value',
  };

  return typeMap[pointType] || '';
}

/**
 * Converts LoRaWAN type abbreviation to full label
 */
export function getLoRaWANTypeLabel(type: string): string {
  switch (type) {
    case LoRaWANPointType.UplinkPayload:
      return 'Uplink Payload';
    case LoRaWANPointType.UplinkMetadata:
      return 'Uplink Metadata';
    default:
      return type;
  }
}

/**
 * Converts LoRaWAN format abbreviation to full label
 */
export function getLoRaWANFormatLabel(format: string): string {
  switch (format) {
    case LoRaWANPointFormat.Numeric:
      return 'Numeric';
    case LoRaWANPointFormat.Boolean:
      return 'Boolean';
    default:
      return format;
  }
}
